import { maybeParseDate } from '@/utils/date-util'
import type {
  NotificationActionResource,
  NotificationMediaPreferencesResource,
  NotificationMediaSettingsResource,
  NotificationPreferencesResource,
  NotificationTypesMediaResource,
  UpdateNotificationPreferencesRequest,
} from '@/features/notifications/api/resources/notification'
import type { NotificationAction } from './Notification'
import type {
  NotificationMediaPreferences,
  NotificationMediumSettings,
  NotificationPreferences,
} from './notification-preferences'

/**
 * Maps what interacting with a notification does.
 */
export function mapNotificationActionResourceToAction(
  resource: NotificationActionResource,
): NotificationAction {
  switch (resource.type) {
    case 'None':
      return { type: 'None' }
    case 'ViewAvailableJobs':
      return { type: 'ViewAvailableJobs' }
    case 'ViewJob':
      return { type: 'ViewJob', jobId: resource.job_id }
  }
  // The API may send values this build doesn't know yet.
  return { type: 'None' }
}

/**
 * Maps a member's notification preferences.
 */
export function mapNotificationPreferencesResourceToPreferences(
  resource: NotificationPreferencesResource,
): NotificationPreferences {
  const typesMedia: NotificationPreferences['typesMedia'] = {}
  for (const [typeId, media] of Object.entries(resource.types_media ?? {})) {
    typesMedia[typeId] = mapNotificationMediaPreferencesResourceToProps(media)
  }

  return {
    media: mapNotificationMediaPreferencesResourceToProps(resource.media),
    typesMedia,
  }
}

/**
 * Maps notification preferences to the request that saves them.
 */
export function mapNotificationPreferencesToRequest(
  preferences: NotificationPreferences,
): UpdateNotificationPreferencesRequest {
  const typesMedia: Partial<NotificationTypesMediaResource> = {}
  for (const [typeId, media] of Object.entries(preferences.typesMedia)) {
    if (media) {
      typesMedia[typeId] = mapNotificationMediaPreferencesToResource(media)
    }
  }

  return {
    media: mapNotificationMediaPreferencesToResource(preferences.media),
    types_media: typesMedia,
  }
}

// The API omits media and mediums that were never set.
function mapNotificationMediaPreferencesResourceToProps(
  resource: Partial<NotificationMediaPreferencesResource> | undefined,
): NotificationMediaPreferences {
  return {
    email: mapNotificationMediaSettingsResourceToProps(resource?.email),
    push: mapNotificationMediaSettingsResourceToProps(resource?.push),
  }
}

function mapNotificationMediaSettingsResourceToProps(
  resource: NotificationMediaSettingsResource | undefined,
): NotificationMediumSettings {
  return {
    enabled: Boolean(resource?.enabled),
    snoozedUntil: maybeParseDate(resource?.snoozed_until),
  }
}

function mapNotificationMediaPreferencesToResource(
  preferences: NotificationMediaPreferences,
): NotificationMediaPreferencesResource {
  return {
    email: mapNotificationMediumSettingsToResource(preferences.email),
    push: mapNotificationMediumSettingsToResource(preferences.push),
  }
}

function mapNotificationMediumSettingsToResource(
  settings: NotificationMediumSettings,
): NotificationMediaSettingsResource {
  return {
    enabled: settings.enabled,
    snoozed_until: settings.snoozedUntil?.toISOString(),
  }
}
